import { useState } from 'react';
import WMIcon from './WMIcon';

const TYPE_CONFIG = {
  sms:   { icon: 'phone',        label: 'SMS',   color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  email: { icon: 'envelope-open', label: 'Email', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  call:  { icon: 'phone',        label: 'Call',  color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  note:  { icon: 'document',     label: 'Note',  color: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400' },
};

const STATUS_DOT = {
  delivered: 'bg-green-500',
  failed:    'bg-red-500',
  pending:   'bg-amber-400',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * CommLog — communication timeline component.
 *
 * Props:
 *   comms     — array of comm entries (newest first)
 *   onAdd(entry) — called when a manual note/call is logged
 *   readOnly  — hides add form
 */
export default function CommLog({ comms = [], onAdd, readOnly = false }) {
  const [type, setType] = useState('note');
  const [body, setBody] = useState('');
  const [expanded, setExpanded] = useState(true);

  const handleAdd = () => {
    if (!body.trim()) return;
    onAdd?.({
      type,
      direction: 'out',
      body: body.trim(),
      sentAt: new Date().toISOString(),
      sentBy: 'Me',
      status: null,
    });
    setBody('');
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Comm Log
          {comms.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-normal">
              {comms.length}
            </span>
          )}
        </span>
        <WMIcon
          name={expanded ? 'chevron-up' : 'chevron-down'}
          className="w-3.5 h-3.5 text-gray-400"
        />
      </button>

      {expanded && (
        <>
          {/* Add form */}
          {!readOnly && (
            <div className="flex gap-2 items-start">
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="text-[var(--wm-text-ui)] border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="note">Note</option>
                <option value="call">Call</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
              <input
                type="text"
                value={body}
                onChange={e => setBody(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="Log a note, call, or message…"
                className="flex-1 text-[var(--wm-text-ui)] border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={handleAdd}
                disabled={!body.trim()}
                className="px-2.5 py-1.5 text-[var(--wm-text-ui)] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Log
              </button>
            </div>
          )}

          {/* Timeline */}
          {comms.length === 0 ? (
            <p className="text-[var(--wm-text-ui)] text-gray-400 dark:text-gray-500 py-2 text-center">No communications yet.</p>
          ) : (
            <div className="space-y-1.5">
              {comms.map((comm, idx) => {
                const cfg = TYPE_CONFIG[comm.type] || TYPE_CONFIG.note;
                return (
                  <div key={comm.id || idx} className="flex items-start gap-2.5 group">
                    {/* Icon */}
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${cfg.color}`}>
                      <WMIcon name={cfg.icon} className="w-2.5 h-2.5" />
                    </div>
                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[var(--wm-text-caption)] font-medium px-1.5 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                        {comm.direction === 'out' && (
                          <span className="text-[var(--wm-text-caption)] text-gray-400">Outbound</span>
                        )}
                        {comm.sentBy && (
                          <span className="text-[var(--wm-text-caption)] text-gray-400">· {comm.sentBy}</span>
                        )}
                        <span className="text-[var(--wm-text-caption)] text-gray-400 ml-auto">{fmtDate(comm.sentAt)}</span>
                      </div>
                      <p className="text-[var(--wm-text-ui)] text-gray-700 dark:text-gray-300 mt-0.5 leading-relaxed">{comm.body}</p>
                      {comm.status && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[comm.status] || 'bg-gray-300'}`} />
                          <span className="text-[var(--wm-text-caption)] text-gray-400 capitalize">{comm.status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
